import { useQuery } from "@tanstack/react-query"
import { App, Divider, Flex, Input, Skeleton, Space } from "antd"
import _ from "lodash"
import { useEffect, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useNavigate } from "react-router"
import { useTheme } from "styled-components"
import { AddressInput, RowButton } from "../components"
import { emptyaddress, RECAPTCHA_SITE_KEY, ShowMessagePage } from "../config"
import { createQuoteRequest, getProducts } from "../endpoints"
import { AddressInterface, QuoteInterface, QuoteItem } from "../types"

export const RequestQuote = (props: {}): JSX.Element => {
  const theme = useTheme()
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const { modal } = App.useApp()
  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token) // Store the token for backend verification
  }
  const [name, setName] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [institution, setInstitution] = useState<string>("")
  const [additionalEmails, setAdditionalEmails] = useState<string>("")
  const [quoteSubmitted, setQuoteSubmitted] = useState<boolean>(false)
  const navigate = useNavigate()
  const [products, setProducts] = useState<QuoteItem[] | null>(null)
  const [requesterAddress, setRequesterAddress] =
    useState<AddressInterface|undefined>(emptyaddress)
  const { isPending, isError, data, error } = useQuery({
    queryKey: ["getproducts"],
    queryFn: getProducts,
    // refetchOnMount:false
  })

  useEffect(() => {
    if (data?.code === 1000) {
      setProducts(
        data.products.map((product) => {
          return { ...product, quantity: 0 }
        })
      )
    }
  }, [data])

  if (isPending) {
    return <Skeleton />
  }

  if (isError) {
    return ShowMessagePage("Error", error.message)
  }

  if (quoteSubmitted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{
          background: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: 8,
          padding: '2em 3em',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          textAlign: 'center',
          maxWidth: 500
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, color: theme.primary, marginBottom: 16 }}>
            check_circle
          </span>
          <h2 style={{ color: theme.primary, marginBottom: 16 }}>Quote Request Received</h2>
          <div style={{ fontSize: '1.1em', marginBottom: 24 }}>
            We have received your quote request.<br />
            We will email you the quote as soon as possible.<br />
            Thank you!
          </div>
          <button
            style={{
              background: theme.primary,
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '0.7em 2em',
              fontSize: '1em',
              cursor: 'pointer',
              marginTop: 8
            }}
            onClick={() => navigate("/")}
          >
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        marginTop: "2.5em",
        maxWidth: 800,
        marginLeft: "auto",
        marginRight: "auto",
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        padding: "2.5em 2em 2em 2em",
      }}
    >
      {/* 1) Align title left with form */}
      <div style={{ marginBottom: "1.5em", textAlign: "left" }}>
        <span style={{ fontSize: "1.2rem", color: theme.primary, fontWeight: "bold", display: 'block' }}>
          Request a Quote
        </span>
      </div>
      <Divider plain style={{ margin: "1.5em 0 1em 0" }}>Choose Products</Divider>
      <div style={{ color: '#888', fontSize: '0.95em', marginBottom: '0.5em', marginLeft: 2 }}>
        Please enter the desired number of samples
      </div>
      <Flex vertical gap="middle">
        {products?.map((product) => (
          <div key={product.id}>
            <Space>
              <Input
                styles={{ input: { width: "10em" } }}
                addonBefore={
                  <div style={{ width: "10em", textAlign: "left" }}>
                    {product.name}
                  </div>
                }
                type="number"
                value={product.quantity}
                onChange={(e) => {
                  const products2 = [...products]
                  const idx = products2.findIndex((p) => p.id === product.id)
                  if (idx !== undefined) {
                    products2[idx] = {
                      ...products2[idx],
                      quantity: Number(e.target.value),
                    }
                    setProducts(products2)
                  }
                }}
                id={product.name}
                name={product.name}
                maxLength={4}
              />
            </Space>
          </div>
        ))}
      </Flex>
      <Divider plain style={{ margin: "2em 0 1em 0" }}>Requester Information</Divider>
      <Flex vertical gap="small">
        <Input
          addonBefore={<div style={{ width: "16em", textAlign: "left" }}>Name</div>}
          type="text"
          onChange={(e) => setName(e.target.value)}
          id="name"
          name="name"
          maxLength={30}
          style={{ marginBottom: 12 }}
        />
        <Input
          addonBefore={<div style={{ width: "16em", textAlign: "left" }}>Email</div>}
          type="text"
          onChange={(e) => setEmail(e.target.value)}
          id="email"
          name="email"
          maxLength={30}
          style={{ marginBottom: 12 }}
        />
        <Input
          addonBefore={<div style={{ width: "16em", textAlign: "left" }}>Institution</div>}
          type="text"
          onChange={(e) => setInstitution(e.target.value)}
          id="institution"
          name="institution"
          maxLength={30}
          style={{ marginBottom: 12 }}
        />
        <Input
          addonBefore={<div style={{ width: "16em", textAlign: "left" }}>Additional Emails to Receive Quote</div>}
          type="text"
          onChange={(e) => setAdditionalEmails(e.target.value)}
          id="addtionalemails"
          name="addtionalemails"
          style={{ marginBottom: 12 }}
        />
      </Flex>
      <Divider plain style={{ margin: "2em 0 1em 0" }}>Requester Address</Divider>
        <AddressInput address={requesterAddress} setAddress={setRequesterAddress} />
      <div style={{ margin: "1.5em 0" }}>
        <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={handleCaptchaChange} />
      </div>
      <div style={{ margin: "2em 0 1em 0", color: theme.primary }}>
        Once we receive your request, we will email you the quote as soon as possible. Thank you!
      </div>
      <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5em" }}>
        <RowButton
          type="primary"
          onClick={async () => {
            if (_.sum(products?.map((x) => x.quantity)) <= 0) {
              modal.warning({ content: "Please choose products" })
            } else if (!name) {
              modal.warning({ content: "Please enter your name" })
            } else if (!email) {
              modal.warning({ content: "Please enter your email" })
            } else if (!requesterAddress?.line1) {
              modal.warning({ content: "Please enter address line 1" })
            } else if (!requesterAddress?.city) {
              modal.warning({ content: "Please enter address city" })
            } else if (!requesterAddress?.state) {
              modal.warning({ content: "Please enter address state" })
            } else {
              const newQuote: QuoteInterface = {
                products: products?.filter((p) => p.quantity > 0) ?? null,
                requesterName: name,
                email,
                additionalEmails,
                institution,
                address: requesterAddress,
              }
              const result = await createQuoteRequest({
                ...newQuote,
                captcha: captchaToken ?? "",
              })
              if (result.code === 1000) {
                setQuoteSubmitted(true)
              } else {
                modal.error({ content: result.message })
              }
            }
          }}
          id="submit"
        >
          Submit
        </RowButton>
      </div>
    </div>
  )
}
